"use client"

import React, { useState, useEffect } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { notificationAPI, PushNotification, SendNotificationDTO } from "../services/marketingService"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { Plus, Send, Users, Truck, Store, User, Bell, Eye, BarChart3 } from "lucide-react"
import {
    Tabs, Card, Flex, Text, Button, Badge, Table, Dialog, TextField, Select, TextArea, Heading
} from "@radix-ui/themes"

const PushNotificationsPage: React.FC = () => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [activeTab, setActiveTab] = useState("send")

    // State
    const [notifications, setNotifications] = useState<PushNotification[]>([])
    const [loading, setLoading] = useState(false)
    const [showDialog, setShowDialog] = useState(false)
    const [stats, setStats] = useState({ totalSent: 0, totalDelivered: 0, totalClicked: 0 })

    // Form State
    const [formData, setFormData] = useState<SendNotificationDTO>({
        title: "",
        body: "",
        targetAudience: "all_users",
        targetUserId: "",
        imageUrl: "",
        actionUrl: ""
    })

    // Fetch Notifications History
    const fetchNotifications = async () => {
        setLoading(true)
        try {
            const response = await notificationAPI.getNotifications()
            setNotifications(response.data.data || [])
        } catch (error) {
            console.error("Failed to load notifications", error)
            // Mock data for development
            setNotifications([
                {
                    _id: "1",
                    title: "New Year Sale!",
                    body: "Get 50% off on your first order",
                    targetAudience: "all_users",
                    sentAt: "2024-01-01T10:00:00Z",
                    sentBy: { _id: "admin1", username: "admin" },
                    recipientCount: 1500,
                    deliveredCount: 1450,
                    clickedCount: 320
                }
            ])
        } finally {
            setLoading(false)
        }
    }

    // Fetch Stats
    const fetchStats = async () => {
        try {
            const response = await notificationAPI.getNotificationStats()
            setStats(response.data.data || { totalSent: 0, totalDelivered: 0, totalClicked: 0 })
        } catch (error) {
            console.error("Failed to load stats", error)
        }
    }

    useEffect(() => {
        fetchNotifications()
        fetchStats()
    }, [])

    const handleSend = async () => {
        if (!formData.title.trim() || !formData.body.trim()) {
            toast({ title: t("error"), description: "Title and message are required", variant: "destructive" })
            return
        }

        if (formData.targetAudience === "specific_user" && !formData.targetUserId?.trim()) {
            toast({ title: t("error"), description: "User ID is required for specific user targeting", variant: "destructive" })
            return
        }

        try {
            await notificationAPI.sendNotification(formData)
            toast({ title: t("success"), description: "Notification sent successfully" })
            setShowDialog(false)
            resetForm()
            fetchNotifications()
            fetchStats()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to send notification", variant: "destructive" })
        }
    }

    const resetForm = () => {
        setFormData({
            title: "",
            body: "",
            targetAudience: "all_users",
            targetUserId: "",
            imageUrl: "",
            actionUrl: ""
        })
    }

    const getAudienceIcon = (audience: string) => {
        switch (audience) {
            case "all_users": return <Users className="w-4 h-4" />
            case "all_drivers": return <Truck className="w-4 h-4" />
            case "all_restaurants": return <Store className="w-4 h-4" />
            case "specific_user": return <User className="w-4 h-4" />
            default: return <Bell className="w-4 h-4" />
        }
    }

    const getAudienceLabel = (audience: string) => {
        const labels: Record<string, string> = {
            all_users: "All Users",
            all_drivers: "All Drivers",
            all_restaurants: "All Restaurants",
            specific_user: "Specific User"
        }
        return labels[audience] || audience
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={t("pushNotifications") || "Push Notifications"}
                subtitle={t("sendNotificationsDesc") || "Send targeted notifications to users and drivers"}
            />

            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Trigger value="send">{t("sendNotification") || "Send Notification"}</Tabs.Trigger>
                    <Tabs.Trigger value="history">{t("history") || "History"}</Tabs.Trigger>
                    <Tabs.Trigger value="stats">{t("statistics") || "Statistics"}</Tabs.Trigger>
                </Tabs.List>

                <div className="mt-4">
                    <Tabs.Content value="send">
                        <Card>
                            <Flex direction="column" gap="4" p="4">
                                <Heading size="4">{t("composeNotification") || "Compose Notification"}</Heading>

                                <label>
                                    <Text as="div" size="2" mb="1" weight="bold">{t("targetAudience") || "Target Audience"}</Text>
                                    <Select.Root value={formData.targetAudience} onValueChange={(value: any) => setFormData({ ...formData, targetAudience: value })}>
                                        <Select.Trigger style={{ width: "100%" }} />
                                        <Select.Content>
                                            <Select.Item value="all_users">
                                                <Flex align="center" gap="2">
                                                    <Users className="w-4 h-4" /> All Users
                                                </Flex>
                                            </Select.Item>
                                            <Select.Item value="all_drivers">
                                                <Flex align="center" gap="2">
                                                    <Truck className="w-4 h-4" /> All Drivers
                                                </Flex>
                                            </Select.Item>
                                            <Select.Item value="all_restaurants">
                                                <Flex align="center" gap="2">
                                                    <Store className="w-4 h-4" /> All Restaurants
                                                </Flex>
                                            </Select.Item>
                                            <Select.Item value="specific_user">
                                                <Flex align="center" gap="2">
                                                    <User className="w-4 h-4" /> Specific User
                                                </Flex>
                                            </Select.Item>
                                        </Select.Content>
                                    </Select.Root>
                                </label>

                                {formData.targetAudience === "specific_user" && (
                                    <label>
                                        <Text as="div" size="2" mb="1" weight="bold">{t("userId") || "User ID"}</Text>
                                        <TextField.Root
                                            value={formData.targetUserId}
                                            onChange={(e) => setFormData({ ...formData, targetUserId: e.target.value })}
                                            placeholder="Enter user ID"
                                            required
                                        />
                                    </label>
                                )}

                                <label>
                                    <Text as="div" size="2" mb="1" weight="bold">{t("title") || "Title"}</Text>
                                    <TextField.Root
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="New Promotion!"
                                        required
                                    />
                                </label>

                                <label>
                                    <Text as="div" size="2" mb="1" weight="bold">{t("message") || "Message"}</Text>
                                    <textarea
                                        value={formData.body}
                                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                        placeholder="Get 50% off on your next order..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md resize-none dark:bg-gray-800 dark:text-white"
                                        rows={4}
                                        required
                                    />
                                </label>

                                <label>
                                    <Text as="div" size="2" mb="1" weight="bold">{t("imageUrl") || "Image URL"} ({t("optional") || "Optional"})</Text>
                                    <TextField.Root
                                        value={formData.imageUrl}
                                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                                        placeholder="https://..."
                                    />
                                </label>

                                <label>
                                    <Text as="div" size="2" mb="1" weight="bold">{t("actionUrl") || "Action URL"} ({t("optional") || "Optional"})</Text>
                                    <TextField.Root
                                        value={formData.actionUrl}
                                        onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                                        placeholder="/promotions/summer-sale"
                                    />
                                </label>

                                <Flex gap="3" mt="2">
                                    <Button onClick={handleSend} size="3">
                                        <Send className="w-4 h-4 mr-2" /> {t("sendNow") || "Send Now"}
                                    </Button>
                                    <Button variant="soft" color="gray" onClick={resetForm}>
                                        {t("reset") || "Reset"}
                                    </Button>
                                </Flex>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="history">
                        <Card>
                            <Flex direction="column" gap="4">
                                <Table.Root variant="surface">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeaderCell>{t("title") || "Title"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("audience") || "Audience"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("sentAt") || "Sent At"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("recipients") || "Recipients"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("delivered") || "Delivered"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("clicked") || "Clicked"}</Table.ColumnHeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {notifications.map(notif => (
                                            <Table.Row key={notif._id}>
                                                <Table.Cell>
                                                    <Text weight="bold">{notif.title}</Text>
                                                    <Text size="1" color="gray" className="block">{notif.body}</Text>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Flex align="center" gap="2">
                                                        {getAudienceIcon(notif.targetAudience)}
                                                        <Text size="2">{getAudienceLabel(notif.targetAudience)}</Text>
                                                    </Flex>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Text size="2">{new Date(notif.sentAt).toLocaleString()}</Text>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color="blue">{notif.recipientCount}</Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color="green">{notif.deliveredCount}</Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color="purple">{notif.clickedCount}</Badge>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                        {notifications.length === 0 && !loading && (
                                            <Table.Row>
                                                <Table.Cell colSpan={6} align="center">
                                                    <Text color="gray">{t("noNotificationsFound") || "No notifications sent yet"}</Text>
                                                </Table.Cell>
                                            </Table.Row>
                                        )}
                                    </Table.Body>
                                </Table.Root>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="stats">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Card>
                                <Flex direction="column" gap="2" p="4">
                                    <Flex align="center" gap="2">
                                        <Send className="w-5 h-5 text-blue-500" />
                                        <Text size="2" color="gray">{t("totalSent") || "Total Sent"}</Text>
                                    </Flex>
                                    <Heading size="6">{stats.totalSent}</Heading>
                                </Flex>
                            </Card>
                            <Card>
                                <Flex direction="column" gap="2" p="4">
                                    <Flex align="center" gap="2">
                                        <Eye className="w-5 h-5 text-green-500" />
                                        <Text size="2" color="gray">{t("totalDelivered") || "Total Delivered"}</Text>
                                    </Flex>
                                    <Heading size="6">{stats.totalDelivered}</Heading>
                                </Flex>
                            </Card>
                            <Card>
                                <Flex direction="column" gap="2" p="4">
                                    <Flex align="center" gap="2">
                                        <BarChart3 className="w-5 h-5 text-purple-500" />
                                        <Text size="2" color="gray">{t("totalClicked") || "Total Clicked"}</Text>
                                    </Flex>
                                    <Heading size="6">{stats.totalClicked}</Heading>
                                </Flex>
                            </Card>
                        </div>
                    </Tabs.Content>
                </div>
            </Tabs.Root>
        </div>
    )
}

export default PushNotificationsPage
